import { inject, Injectable, Signal, signal } from '@angular/core';
import { map, Observable, of, tap } from 'rxjs';

import { Role } from '@auth/enums/role.enum';
import { UserOrganization } from '@auth/interfaces/user-organization.interface';
import { UserInOrganizationSchema } from '@graphql/rbs/schemas/user-in-organization.schema';
import { UsersService } from '@graphql/rbs/services/users.service';
import { AuthenticationService } from 'src/app/auth/services/authentication.service';

export const STORED_USER_ORGANIZATION_KEY = 'user-organization';

@Injectable({ providedIn: 'root' })
export class RbsService {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly usersService = inject(UsersService);

  private readonly _userOrganizationList = signal<UserOrganization[]>([]);
  private readonly _selectedUserOrganization = signal<UserOrganization | null>(null);

  set selectedUserOrganization(selectedUserOrganization: UserOrganization) {
    this._selectedUserOrganization.set(selectedUserOrganization);
    this.storeSelectedUserOrganization(selectedUserOrganization);
  }

  get selectedUserOrganization(): Signal<UserOrganization | null> {
    return this._selectedUserOrganization.asReadonly();
  }

  get userOrganizationList(): Signal<UserOrganization[]> {
    return this._userOrganizationList.asReadonly();
  }

  constructor() {
    this.authenticationService.refreshToken().subscribe();
  }

  fetchUserOrganizationList(): Observable<UserOrganization[]> {
    if (this._userOrganizationList().length > 0) {
      return of(this._userOrganizationList());
    }

    if (!this.authenticationService.userId) {
      return of([]);
    }

    return this.usersService.getUserById(this.authenticationService.userId).pipe(
      map(({ data }) => this.mapUserOrganizationList(data.userById?.usersInOrg ?? [])),
      map((list) => list.filter(({ role }) => Object.values(Role).includes(role))),
      map((list) => list.toSorted((a, b) => a.role.localeCompare(b.role))),
      map((list) => list.toSorted((a, b) => a.organizationName.localeCompare(b.organizationName))),
      tap((list) => this._userOrganizationList.set(list)),
    );
  }

  private mapUserOrganizationList(userInOrgList: UserInOrganizationSchema[]): UserOrganization[] {
    return userInOrgList.flatMap((userInOrg) => {
      const organization = userInOrg.org;
      const organizationRoles = userInOrg.orgUsersForOrgRole;

      if (!organizationRoles || organizationRoles.length === 0) {
        return [];
      }

      return organizationRoles.map((organizationRole) => ({
        organizationName: organization?.orgName ?? '',
        tenantId: userInOrg.orgId ?? '',
        role: (organizationRole.orgRole?.serviceRole?.name ?? '') as Role,
        roleId: organizationRole.orgRole?.serviceRoleId ?? '',
      }));
    });
  }

  verifyStoredUserOrganization(): void {
    const storedUserOrganization = sessionStorage.getItem(STORED_USER_ORGANIZATION_KEY);

    if (!storedUserOrganization) {
      return;
    }

    const organizationSaved: Pick<UserOrganization, 'roleId' | 'tenantId'> =
      JSON.parse(storedUserOrganization);
    const matchedUserOrganization = this._userOrganizationList().find(
      ({ roleId, tenantId }) =>
        roleId === organizationSaved?.roleId && tenantId === organizationSaved.tenantId,
    );

    if (matchedUserOrganization) {
      this.selectedUserOrganization = matchedUserOrganization;
      return;
    }

    sessionStorage.removeItem(STORED_USER_ORGANIZATION_KEY);
  }

  private storeSelectedUserOrganization(selectedUserOrganization: UserOrganization): void {
    const { tenantId, roleId } = selectedUserOrganization;
    sessionStorage.setItem(STORED_USER_ORGANIZATION_KEY, JSON.stringify({ tenantId, roleId }));
  }
}
